<template>
  <div>
    <UiSingleSelect
      v-model="currentRole"
      title="ROLE"
      :hide-title="isTabView"
      label="title"
      :is-position-top="true"
      reduce="value"
      :hide-un-select-icon="true"
      :no-error="true"
      :disabled="filteredRoles.length === 1"
      :options="filteredRoles"
      @change="switchRoles(currentRole)"
    />
  </div>
</template>

<script>
import UiSingleSelect from '@/src/components/UiElements/UiSingleSelect.vue'
import { ROLES_LIST, TENANT_ROLES } from '@src/constants/user-roles-constants.js'
import { mapGetters, mapActions, mapState } from 'vuex'
import { getDefaultRoute } from '@utils/generalUtil'
import { isTawkToAllowed, showTawkWidget, hideTawkWidget } from '@src/utils/widgetUtil.js'
export default {
  components: {
    UiSingleSelect,
  },
  data() {
    return {
      currentRole: '',
      isClassSectionNullFor: [TENANT_ROLES.CAMPUS_ADMIN],
    }
  },
  computed: {
    ...mapGetters('auth', ['userRoles']),
    ...mapGetters('layout', ['isMultipleCampusesAllowed']),
    ...mapState({
      isAssociatedStudentProfileActive: (state) => state.layout.isAssociatedStudentProfileActive,
      activeRole: (state) => state.layout.activeRole,
      isTabView: (state) => state.layout.isTabView,
    }),

    filteredRoles() {
      const isControlPanel = this.$route.name?.startsWith('control-panel')
      return ROLES_LIST.filter((role) => {
        if (isControlPanel) {
          return [TENANT_ROLES.CAMPUS_ADMIN, TENANT_ROLES.SUPER_ADMIN].includes(role.value)
        }
        return this.userRoles?.some((user) => user.role_id === role.value)
      })
    },
    isSuperAdmin() {
      return this.activeRole === TENANT_ROLES.SUPER_ADMIN
    },
    isPickupPerson() {
      return this.activeRole === TENANT_ROLES.PICKUP_PERSON
    },
    isGuardian() {
      return this.activeRole === TENANT_ROLES.GUARDIAN
    },
    currentStudent() {
      return JSON.parse(localStorage.getItem('currentActiveStudent'))
    },
  },
  mounted() {
    this.currentRole = this.activeRole
  },
  methods: {
    /**
     * Switch Roles
     * @param {number} id - role_id
     * @returns {void}
     * @description: Function is getting ID and switch the role for example: section_teacher and super_admin
     * and providing relevant access according to the role.
     */

    async switchRoles(role, userSwitch = true, getStudents = true) {
      if (this.activeRole === role && userSwitch) return false

      this.setRoles(role)
      this.toggleTawkWidgetForRole()
      this.$store.commit('layout/SET_APP_LOADING', true)

      await this.prepareGuardianContext(getStudents)

      const campusList = await this.fetchCampuses()
      if (this.isSuperAdmin) {
        this.handleSuperAdminFlow(campusList)
        return
      }

      const selectedCampus = this.setDefaultCampus(campusList)
      if (this.shouldSkipClassSectionFlow()) {
        this.finalizeRoleSwitch({ campusList, selectedCampus })
        return
      }

      const { classList, selectedClass, sectionList, selectedSection } =
        await this.loadClassAndSectionData(selectedCampus)

      this.finalizeRoleSwitch({
        campusList,
        classList,
        sectionList,
        selectedCampus,
        selectedClass,
        selectedSection,
      })
    },

    async prepareGuardianContext(getStudents) {
      if (!this.isGuardian || !getStudents) return
      await this.getActiveGuardianStudents()
      if (this.currentStudent) {
        this.$store.commit('layout/SET_GUARDIAN_AND_STUDENT_PROFILES', true)
      }
    },

    async fetchCampuses() {
      const response = await this.getCampusList()
      return response?.data || []
    },

    handleSuperAdminFlow(campusList) {
      const selectedCampus =
        !this.isMultipleCampusesAllowed && campusList.length ? campusList[0] : ''
      if (selectedCampus) {
        localStorage.setItem('currentCampusScope', JSON.stringify(selectedCampus))
      }
      this.finalizeRoleSwitch({ campusList, selectedCampus })
    },

    setDefaultCampus(campusList) {
      const campus = campusList[0] || ''
      localStorage.setItem('currentCampusScope', JSON.stringify(campus))
      return campus
    },

    shouldSkipClassSectionFlow() {
      const guardianWithoutStudent = this.isGuardian && !this.isAssociatedStudentProfileActive
      return guardianWithoutStudent || this.isPickupPerson
    },

    async loadClassAndSectionData(selectedCampus) {
      if (!selectedCampus || this.isClassSectionNullFor.includes(this.activeRole)) {
        return {
          classList: [],
          selectedClass: '',
          sectionList: [],
          selectedSection: '',
        }
      }

      const classList = await this.getUserClasses()
      const selectedClass = classList[0] || ''
      localStorage.setItem('currentClassScope', JSON.stringify(selectedClass))

      if (!selectedClass) {
        return { classList, selectedClass, sectionList: [], selectedSection: '' }
      }

      const sectionListResponse = await this.getUserSpecificSectionsList()
      const sectionList = sectionListResponse?.data || []
      const selectedSection = sectionList[0] || ''

      return { classList, selectedClass, sectionList, selectedSection }
    },

    finalizeRoleSwitch({
      campusList = [],
      classList = [],
      sectionList = [],
      selectedCampus = '',
      selectedClass = '',
      selectedSection = '',
    }) {
      this.redirectToAuthenticPage(selectedCampus, selectedClass, selectedSection)
      this.$store.commit('layout/SET_CAMPUSES', campusList)
      this.$store.commit('classes/SET_USERS_CLASS_LIST', classList)
      this.$store.commit('section/SET_CLASS_SECTIONS', sectionList)
    },

    toggleTawkWidgetForRole() {
      if (isTawkToAllowed()) {
        showTawkWidget()
      } else {
        hideTawkWidget()
      }
    },

    redirectToAuthenticPage(selectedCampus = '', selectedClass = '', selectedSection = '') {
      const activeRole = this.$store.state.layout.activeRole
      const route = getDefaultRoute(activeRole, selectedCampus, selectedSection)
      const hasStudentId = 'student_id' in this.$route.query
      const isExcludedRole = ![
        TENANT_ROLES.SUPER_ADMIN,
        TENANT_ROLES.CAMPUS_ADMIN,
        TENANT_ROLES.SECTION_TEACHER,
      ].includes(this.activeRole)

      const isIncludeRole = [
        TENANT_ROLES.SUPER_ADMIN,
        TENANT_ROLES.CAMPUS_ADMIN,
        TENANT_ROLES.STAFF,
        TENANT_ROLES.SECTION_TEACHER,
        TENANT_ROLES.PICKUP_PERSON,
        TENANT_ROLES.GUARDIAN,
        TENANT_ROLES.SECTION_STUDENT,
      ].includes(this.activeRole)

      if ((hasStudentId && isExcludedRole) || isIncludeRole) {
        this.$router?.push({ name: route })
      }

      setTimeout(() => {
        this.$store.commit('layout/SET_INSTITUTES_HIERARCHY', {
          selectedCampus,
          selectedClass,
          selectedSection,
        })
        this.$store.commit('layout/SET_APP_LOADING', false)
      }, 1500)
    },
    ...mapActions('layout', [
      'setRoles',
      'getActiveGuardianStudents',
      'getUserSpecificSectionsList',
      'getCampusList',
    ]),
    ...mapActions('classes', ['getUserClasses']),
  },
}
</script>
